import { Duration } from 'luxon';
import { guards } from '@shared/helpers/typeguards.helpers';
import { createLocalStorageRecordStore } from './local-storage.helpers';
import { isViteHotReloadEnabled } from './vite.helpers';

const ERROR_RELOAD_GUARD_MS = Duration.fromObject({ minutes: 2 }).as(
  'milliseconds',
);

const { getItem, setItem } = createLocalStorageRecordStore<string, number>(
  'error_reload_guard',
);

export type ErrorRecoveryMode = 'hmr' | 'reload';

/**
 * `hmr` during `npm run dev` (Vite hot client). `reload` in production builds.
 * Callers skip the 2-minute error-page lock and `window.location.reload` in `hmr`.
 */
export const getErrorRecoveryMode = (): ErrorRecoveryMode =>
  isViteHotReloadEnabled() ? 'hmr' : 'reload';

const errorMessageOf = (error: Error): string =>
  String(error?.message ?? error ?? 'Unknown error');

type ErrorRecoveryOptions = {
  mode?: ErrorRecoveryMode;
};

/**
 * True when the same render error already triggered a recovery reload recently.
 * Always false in `hmr` mode so a Fast Refresh failure cannot lock the error page.
 */
export const hasRecentSameErrorFor = (
  error: Error,
  options: ErrorRecoveryOptions = {},
): boolean => {
  const mode = options.mode ?? getErrorRecoveryMode();
  if (mode === 'hmr') return false;
  if (guards.is.undefined(window)) return false;

  const timestamp = getItem(errorMessageOf(error));
  return Boolean(
    timestamp &&
    Number.isFinite(timestamp) &&
    Date.now() - timestamp < ERROR_RELOAD_GUARD_MS,
  );
};

type RecordAndReloadOptions = ErrorRecoveryOptions & {
  reload?: () => void;
};

/**
 * Remember the error and reload once. No-ops in `hmr` mode — a full reload
 * drops in-memory Auth0 state and the 2-minute lock then shows the error page.
 */
export const recordAndReload = (
  error: Error,
  options: RecordAndReloadOptions = {},
): void => {
  const mode = options.mode ?? getErrorRecoveryMode();
  if (mode === 'hmr') return;
  if (guards.is.undefined(window)) return;

  setItem(errorMessageOf(error), Date.now());
  const reload = options.reload ?? (() => window.location.reload());
  reload();
};
